import { useCallback, useEffect, useState } from 'react';
import { toast } from '@drbell/ui';
import { api, ApiError, type CaringEntry, type Channel, type InvitePreview, type PendingInvite } from '../api.ts';
import {
  answerRingingRequest, approveInvite, failureOf, InviteCard, patientOf, rejectInvite, RingingRequestCard, type AnswerFailure,
} from './InviteCard.tsx';

/**
 * Invitations, on the web — RX-CARE-02.
 *
 * Accepting was phone-only. A son who looks after his mother from a work
 * laptop, and never installs the app, signed in here and was told "open the
 * link you were sent" — a link that opens the phone app. He could never
 * join, so the escalation ladder never reached him.
 *
 * Claim first: an invitation is held against a phone number, and it only
 * shows up here once this account has proved it owns that number.
 *
 * Each one is the same Approve / Reject prompt the /i/:token link opens —
 * who asked, what is shared, which alerts and how — not a one-line card, so
 * answering here is as informed as answering from the link.
 *
 * The family's other question waits here too: "ring {patient}'s reminders
 * on your phone?", for somebody already helping (ringing needs both sides).
 * Its push opens the phone app; on the web, this is where it is answered,
 * as well as in How I'm told.
 */
interface Item {
  invite: PendingInvite;
  token: string;
  preview: InvitePreview;
}

export function Invitations({ onAccepted }: { onAccepted: (patientId?: string) => void }) {
  const [items, setItems] = useState<Item[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failures, setFailures] = useState<Record<string, AnswerFailure>>({});
  const [requests, setRequests] = useState<CaringEntry[]>([]);

  const loadRequests = useCallback(async () => {
    const r = await api.get<{ caring: CaringEntry[] }>('/api/me/caring').catch(() => ({ caring: [] as CaringEntry[] }));
    setRequests(r.caring.filter((c) => c.ringingRequest));
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const answer = async (entry: CaringEntry, accept: boolean) => {
    setBusy(entry.grantId);
    try {
      await answerRingingRequest(entry.grantId, accept);
      toast.success(
        accept
          ? `Accepted. ${entry.patientName}’s reminders will ring on your phone.`
          : `Declined. ${entry.patientName}’s family can see your answer.`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) toast.info('This request has ended or was withdrawn.');
      else toast.error('Could not answer', e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
      await loadRequests();
    }
  };

  const load = useCallback(async () => {
    try {
      await api.post('/api/invites/claim').catch(() => undefined);
      const { invites } = await api.get<{ invites: PendingInvite[] }>('/api/invites');
      const loaded = await Promise.all(
        invites.map(async (invite) => {
          if (!invite.inviteToken) return null;
          // One that has ended since the list was read has nothing to answer.
          const preview = await api
            .get<InvitePreview>(`/api/invites/${invite.inviteToken}`)
            .catch(() => null);
          return preview ? { invite, token: invite.inviteToken, preview } : null;
        }),
      );
      setItems(loaded.filter((x): x is Item => x !== null));
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (item: Item, accept: boolean, channels?: Channel[]) => {
    setBusy(item.invite.id);
    setFailures(({ [item.invite.id]: _gone, ...rest }) => rest);
    try {
      if (accept) {
        const r = await approveInvite(item.token, channels, item.preview.ringsHere);
        toast.success(
          'linkedAsSelf' in r ? 'Your reminders will come to you' : `You can now see ${patientOf(item.preview)}`,
        );
        await load();
        onAccepted(r.patientId);
      } else {
        await rejectInvite(item.token);
        toast.success('Rejected');
        await load();
      }
    } catch (e) {
      const failure = failureOf(e);
      if (failure.kind === 'ended') {
        toast.info('That invitation has ended or was withdrawn.');
        await load();
      } else {
        setFailures((f) => ({ ...f, [item.invite.id]: failure }));
      }
    } finally {
      setBusy(null);
    }
  };

  if ((!items || items.length === 0) && requests.length === 0) return null;

  return (
    <section className="mb-6 space-y-3" aria-label="Invitations">
      {requests.map((entry) => (
        <RingingRequestCard
          key={entry.grantId}
          entry={entry}
          busy={busy !== null}
          onAnswer={(accept) => void answer(entry, accept)}
        />
      ))}
      {(items ?? []).map((item) => {
        const failure = failures[item.invite.id];
        return (
          <InviteCard
            key={item.invite.id}
            token={item.token}
            preview={item.preview}
            busy={busy !== null}
            onApprove={(channels) => void decide(item, true, channels)}
            onReject={() => void decide(item, false)}
            problem={
              failure && (
                <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
                  {failure.kind === 'no_channel'
                    ? 'Keep at least one way to be told.'
                    : failure.kind === 'phone_mismatch'
                      ? `This invitation was sent to ${item.preview.invitePhoneHint ?? 'a different number'}. Sign in with that number to answer it.`
                      : failure.kind === 'other'
                        ? failure.message
                        : null}
                </p>
              )
            }
          />
        );
      })}
    </section>
  );
}
